/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { BEM } from "@adapptio/lib-components";
import React from "react";

import "./FieldWrapper.scss";

export interface IFieldWrapperProps extends React.ComponentPropsWithoutRef<"div"> {
	type: string;
	className?: string;
	grow?: boolean;
	isFocused?: boolean;
	onClick?: React.MouseEventHandler<HTMLDivElement>;
	children?: React.ReactNode;
}

const FieldWrapper = React.forwardRef<HTMLDivElement, IFieldWrapperProps>(
	({ type, className, grow, isFocused, children, onClick, ...props }, ref) => {
		const baseClass = BEM("edt-field", null, {
			grow: grow
		});

		baseClass.mod("type-" + type);
		baseClass.add("edt-field-element");
		baseClass.add("edt-field-element--input");

		if (isFocused) {
			baseClass.add("edt-field-element--is-focused");
		}

		if (className) {
			baseClass.add(className);
		}

		return (
			<div className={baseClass.toClassName()} ref={ref} onClick={onClick} {...props}>
				{children}
			</div>
		);
	}
);

FieldWrapper.displayName = "FieldWrapper";

const FieldWrapperMemo = React.memo(FieldWrapper);

export { FieldWrapperMemo as FieldWrapper };
